import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { appConstants } from '../_constants/app.constants';
import { setAccessToken, getAccessToken } from '../setupAxios';
import { clearFavoriteTournamentsLocal } from '../_utils/favoritesStorage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const res = await axios.get(`${appConstants.API_URL}/api/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const res = await axios.post(`${appConstants.API_URL}/api/auth/login`, {
      email,
      password,
    });
    if (res.data?.accessToken) {
      setAccessToken(res.data.accessToken);
    }
    setUser(res.data?.user ?? null);
    return res.data;
  };

  const register = async ({ email, password, firstName, lastName, organizationName }) => {
    const body = { email, password, firstName, lastName };
    if (organizationName) {
      body.organizationName = organizationName;
    }
    const res = await axios.post(`${appConstants.API_URL}/api/auth/register`, body);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${appConstants.API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore: aun así limpiamos cliente y redirigimos */
    }
    setAccessToken(null);
    setUser(null);
    clearFavoriteTournamentsLocal();
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    if (typeof window !== 'undefined') {
      window.location.assign('/');
    }
  };

  const value = {
    user,
    ready,
    login,
    register,
    logout,
    reloadUser: loadMe,
    isAuthenticated: Boolean(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
